import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import AppLayout from "@/components/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import NewInterventionPage from "@/pages/NewInterventionPage";
import InterventionDetailPage from "@/pages/InterventionDetailPage";
import PrintLabelPage from "@/pages/PrintLabelPage";
import SearchPage from "@/pages/SearchPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import ProfilePage from "@/pages/ProfilePage";
import UsersPage from "@/pages/UsersPage";
import BackupPage from "@/pages/BackupPage";
import TasksPage from "@/pages/TasksPage";
import HomeInterventionsPage from "@/pages/HomeInterventionsPage";
import WorkshopInterventionsPage from "@/pages/WorkshopInterventionsPage";
import NewHomeInterventionPage from "@/pages/NewHomeInterventionPage";
import ReceiptPage from "@/pages/ReceiptPage";
import CalendarPage from "@/pages/CalendarPage";
import ServicesExpiringPage from "@/pages/ServicesExpiringPage";
import AssistancePage from "@/pages/AssistancePage";
import AssistanceCustomerPage from "@/pages/AssistanceCustomerPage";
import ModulesPage from "@/pages/ModulesPage";
import ModuleRunner from "@/pages/modules/ModuleRunner";
import PublicRequestPage from "@/pages/PublicRequestPage";
import TicketsPage from "@/pages/TicketsPage";
import Setup2faPage from "@/pages/Setup2faPage";
import "@/App.css";

/**
 * Protected + Force2fa gate. Requires authenticated user AND 2FA enabled.
 * If user is authenticated but 2FA not enabled → redirect to /setup-2fa.
 * The /setup-2fa page itself skips this check (handled in <Force2fa> below).
 */
function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [twoFa, setTwoFa] = useState({ loading: true, enabled: false });

  useEffect(() => {
    if (!user) { setTwoFa({ loading: false, enabled: false }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/2fa/status");
        if (!cancelled) setTwoFa({ loading: false, enabled: !!data.enabled });
      } catch {
        if (!cancelled) setTwoFa({ loading: false, enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (twoFa.loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Verifica sicurezza…</div>;
  if (!twoFa.enabled) return <Navigate to="/setup-2fa" state={{ from: loc.pathname + loc.search }} replace />;
  return children;
}

function Setup2faOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function GatedApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
        <Route path="/setup-2fa" element={<Setup2faOnly><Setup2faPage /></Setup2faOnly>} />
        <Route element={<Protected><AppLayout /></Protected>}>
          <Route index element={<DashboardPage />} />
          <Route path="interventi-laboratorio" element={<WorkshopInterventionsPage />} />
          <Route path="interventi/nuovo" element={<NewInterventionPage />} />
          <Route path="interventi/:id" element={<InterventionDetailPage />} />
          <Route path="ricerca" element={<SearchPage />} />
          <Route path="clienti" element={<CustomersPage />} />
          <Route path="clienti/:id" element={<CustomerDetailPage />} />
          <Route path="attivita" element={<TasksPage />} />
          <Route path="domicilio" element={<HomeInterventionsPage />} />
          <Route path="domicilio/nuovo" element={<NewHomeInterventionPage />} />
          <Route path="calendario" element={<CalendarPage />} />
          <Route path="scadenzario" element={<ServicesExpiringPage />} />
          <Route path="assistenze" element={<AssistancePage />} />
          <Route path="assistenze/:id" element={<AssistanceCustomerPage />} />
          <Route path="ticket" element={<TicketsPage />} />
          <Route path="moduli" element={<ModulesPage />} />
          <Route path="moduli/:moduleId" element={<ModuleRunner />} />
          <Route path="profilo" element={<ProfilePage />} />
          <Route path="utenti" element={<AdminOnly><UsersPage /></AdminOnly>} />
          <Route path="backup" element={<AdminOnly><BackupPage /></AdminOnly>} />
        </Route>
        <Route path="interventi/:id/etichetta" element={<Protected><PrintLabelPage /></Protected>} />
        <Route path="interventi/:id/scontrino" element={<Protected><ReceiptPage /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Fully public route — no auth */}
            <Route path="/richiesta-assistenza" element={<PublicRequestPage />} />
            {/* All other routes go through auth + 2FA gate */}
            <Route path="*" element={<GatedApp />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}
